/**
 * GuestAnswers — one registrant's answers, as they were given.
 *
 * Data: bound — ops: getEventGuestAnswers
 * (GET /v1/admin/events/{eventId}/guests/{guestId}/answers, CC 0058 option A).
 * Proven live by CC on the demo tenant (Registrations list, 28 Sept). A failed
 * read shows its load error, never an empty list dressed as "no answers".
 *
 * Every answer is rendered from its own SNAPSHOT — label, options, counter rows —
 * frozen when the registrant submitted it, never from the event's current
 * questions or the live library. So an answer still reads correctly after its
 * question is renamed, edited or detached (operator ruling, 28 Sept: staff see
 * answers before, during and after the event). A sealed answer arrives with
 * `sealed: true` and no value, and reads "Hidden".
 *
 * Files are listed by name. Opening one goes through the owner-capability
 * read (getQuestionAnswerFile), which is not wired here yet; a 403 there means
 * "you cannot open this file", so no link is offered rather than one that fails.
 */
import { useEffect, useState } from "react";
import { T } from "@v0y4ge/jrni-ui";
import { getEventGuestAnswers } from "@v0y4ge/api-client";
import { useT } from "../../lib/i18n";

type Opt = { id: string; label: string };
type Row = { id: string; label?: string };
type Answer = {
  questionId: string;
  value?: unknown;
  sealed?: boolean;
  files?: { fileId: string; name: string; scanStatus: string }[];
  snapshot: { label: string; type: string; options?: Opt[]; stepperRows?: Row[] };
  answeredAt: string;
};

/** The answer's text, from the snapshot alone. "" when there is nothing to show. */
export function answerText(a: Answer, t: (k: string, f: string) => string): string {
  if (a.sealed) return t("guests.answers.hidden", "Hidden");
  const type = String(a.snapshot?.type || "");
  const v = a.value;
  const opts = a.snapshot?.options || [];
  const optLabel = (id: string) => opts.find((o) => o.id === id)?.label || id;
  if (type === "file") {
    const names = (a.files || []).map((f) => f.name).filter(Boolean);
    return names.length ? names.join(", ") : Array.isArray(v) ? t("guests.answers.filesCount", "File uploaded") : "";
  }
  if (v === null || v === undefined || v === "") return "";
  if (typeof v === "boolean") return v ? t("common.yes", "Yes") : t("common.no", "No");
  if (Array.isArray(v)) return v.map((x) => optLabel(String(x))).join(", ");
  if (typeof v === "object") {
    const rows = a.snapshot?.stepperRows || [];
    return Object.entries(v as Record<string, unknown>)
      .map(([rid, n]) => { const r = rows.find((x) => x.id === rid); return (r && r.label ? r.label + " " : "") + String(n); })
      .join(" · ");
  }
  if (type === "datetime") {
    const d = new Date(String(v));
    return isNaN(d.getTime()) ? String(v) : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  }
  if (type === "radio" || type === "select") return optLabel(String(v));
  return String(v);
}

export function GuestAnswers({ eventId, guestId }: { eventId: string; guestId: string }) {
  const t = useT();
  const [state, setState] = useState<{ phase: "loading" | "ready" | "error"; rows: Answer[] }>({ phase: "loading", rows: [] });
  useEffect(() => {
    let live = true;
    setState({ phase: "loading", rows: [] });
    getEventGuestAnswers(eventId, guestId)
      .then((res) => { if (live) setState({ phase: "ready", rows: ((res && res.data) || []) as Answer[] }); })
      .catch((e: unknown) => { console.warn("getEventGuestAnswers failed:", e); if (live) setState({ phase: "error", rows: [] }); });
    return () => { live = false; };
  }, [eventId, guestId]);

  const box: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 8, padding: "10px 12px", background: T.n5, borderTop: `1px solid ${T.border}` };
  if (state.phase === "loading") return <div style={{ ...box, fontSize: 12, color: T.soft }}>{t("common.states.loading", "Loading…")}</div>;
  if (state.phase === "error") return <div role="alert" style={{ ...box, fontSize: 12, color: "#8C1D18" }}>{t("guests.answers.readFailed", "Couldn't load this registrant's answers.")}</div>;
  const rows = state.rows.filter((a) => a && a.snapshot && a.snapshot.type !== "heading" && a.snapshot.type !== "label");
  if (!rows.length) return <div style={{ ...box, fontSize: 12, color: T.soft }}>{t("guests.answers.none", "No answers were given with this registration.")}</div>;
  return (
    <div style={box}>
      {rows.map((a) => {
        const text = answerText(a, t);
        return (
          <div key={a.questionId} style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(0, 3fr)", gap: 12, fontSize: 12.5 }}>
            <span style={{ color: T.soft, overflowWrap: "anywhere" }}>{a.snapshot.label}</span>
            <span style={{ color: a.sealed || !text ? T.soft : T.n1, fontStyle: a.sealed ? "italic" : "normal", overflowWrap: "anywhere" }}>{text || "—"}</span>
          </div>
        );
      })}
    </div>
  );
}
